/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react'
import {
  useLazyGetUserDetailsByIdQuery,
  useLazyGetUsersQuery,
  useUpdateUserAccountStateMutation,
} from '../../api/app/UserManagementApiService'
import { useDispatch, useSelector } from 'react-redux'
import { addUserData } from '../../redux/reducers/UserRolesReducer'
import { USER_ACTION, USERS_TYPES } from '../../utils/constants/UIConstants'
import { useTranslation } from 'react-i18next'
import { showToast } from '../../components/common/toast/Toast'
import { usersListData } from '../../interface/response/UserRolesResponse'
import { RootState } from '../../store/Store'

const useAddUserHook = () => {
  const offSet = 0
  const dispatch = useDispatch()
  const { t } = useTranslation(['translation'])
  const [showAddUser, setShowAddUser] = useState(false)
  const [updateUserStatus, setUpdateUserStatus] = useState(false)
  const [limit, setLimit] = useState(10)
  const [pageNumber, setPageNumber] = useState(1)
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState(true)
  const [userInfo, setUserInfo] = useState<usersListData | undefined>(undefined)
  const [userId, setUserId] = useState('')
  const [isEdit, setIsEdit] = useState(false)
  const [showActivityLog, setShowActivityLog] = useState(false)
  const [activityLogUserId, setActivityLogUserId] = useState<number>(0)
  const [activityLogUserName, setActivityLogUserName] = useState<string>('')
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight)
  const [getUsersList, usersList] = useLazyGetUsersQuery()
  const [updateUserAccountStatus] = useUpdateUserAccountStateMutation()
  const [getUserDetailsById] = useLazyGetUserDetailsByIdQuery()
  const componentsIdMapping = useSelector(
    (state: RootState) => state.atlas.widgetIdMapping,
  )
  const showAddUserPopUp = () => {
    setShowAddUser(true)
    // dispatch(
    //   addUserData({
    //     data: value,
    //     type: USER_ACTION.ADD_USER_TYPE,
    //   }),
    // )
  }
  useEffect(() => {
    handleApplyFilter()
  }, [limit])

  async function fetchUserDataAsync(uId: string) {
    try {
      const userDetailsById = await getUserDetailsById(parseInt(uId))
      if (userDetailsById?.data) {
        const data = {
          userName: userDetailsById?.data?.userName,
          email: userDetailsById?.data?.email,
          phone: userDetailsById?.data?.phone ?? '',
        }
        const addingUserType = userDetailsById?.data?.isExternalUser
          ? USERS_TYPES.EXTERNAL_USER
          : USERS_TYPES.ATLAS_USER
        dispatch(
          addUserData({
            data: addingUserType,
            type: USER_ACTION.ADD_USER_TYPE,
          }),
        )
        dispatch(
          addUserData({
            data: data,
            type: USER_ACTION.ADD_USER_INFO,
          }),
        )
        dispatch(
          addUserData({
            data: userDetailsById?.data?.roles,
            type: USER_ACTION.ADD_USER_ROLES,
          }),
        )
        dispatch(
          addUserData({
            data: userDetailsById?.data?.customerCode,
            type: USER_ACTION.ADD_CUSTOMER_SELECTION,
          }),
        )
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight)
    }
    window.addEventListener('resize', handleResize)
    window.visualViewport?.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.visualViewport?.removeEventListener('resize', handleResize)
    }
  }, [])

  const onStatusChangeHandler = (value: boolean) => {
    setStatus(value)
    getUsersList({
      offSet: offSet,
      limit,
      searchQuery: query,
      userStatus: value,
    })
  }
  const queryHandler = (e: React.SyntheticEvent) => {
    setQuery((e.target as HTMLInputElement).value)
  }

  const handleResetFilter = useCallback(() => {
    setQuery('')
    setStatus(true)
    setPageNumber(1)
    setLimit(10)
    getUsersList({
      offSet: 0,
      limit,
      userTypeId: 1,
      searchQuery: '',
      userStatus: true,
    })
  }, [])

  const closeAddUserPopUp = () => {
    setShowAddUser(false)
    dispatch(addUserData({ type: USER_ACTION.REMOVE_USER_TYPE }))
    dispatch(addUserData({ type: USER_ACTION.REMOVE_USER_INFO }))
    dispatch(addUserData({ type: USER_ACTION.REMOVE_USER_ROLES }))
    dispatch(addUserData({ type: USER_ACTION.REMOVE_HAULER_SELECTION }))
    dispatch(addUserData({ type: USER_ACTION.REMOVE_CUSTOMER_SELECTION }))
    setIsEdit(false)
  }

  const updateUserStatusPopUp = () => {
    setUpdateUserStatus(!updateUserStatus)
  }

  const displayErrorToast = (message: string, code: string) => {
    showToast(`${message}(${code})`, 'error')
  }

  const handlePageSize = useCallback((val: number) => {
    setLimit(val)
  }, [])

  const fetchData = useCallback(
    (page: number) => {
      setPageNumber(page)
      const offsetValue = limit * (page - 1)
      getUsersList({
        offSet: offsetValue,
        limit,
        searchQuery: query,
        userStatus: status,
      })
    },
    [limit, query, status],
  )

  const onEditHandler = (Id: string) => {
    setUserId(Id)
    setIsEdit(true)
    setShowAddUser(!showAddUser)
    if (Id) {
      fetchUserDataAsync(Id)
    }
  }

  const getUserModalMessage = () => {
    if (userInfo?.userStatus) {
      return t('userRoles.inactive_user_message')
    } else {
      return t('userRoles.active_user_message')
    }
  }

  const updateUserAccountState = () => {
    if (!userInfo) return
    updateUserAccountStatus({
      userId: userInfo.userId,
      accountEnabled: !userInfo.userStatus,
    })
      .unwrap()
      .then((response) => {
        if (response?.requestStatus) {
          userInfo.userStatus
            ? showToast(t(`messages.user_activated_success_message`))
            : showToast(t(`messages.user_inactivated_success_message`))
        } else {
          displayErrorToast(t(`messages.${response?.code}`), response?.code)
        }
      })
      .catch((error) =>
        displayErrorToast(t(`messages.${error?.status}`), error?.status),
      )
    updateUserStatusPopUp()
    setUserInfo(undefined)
  }

  const onStatusHandler = (userData: usersListData) => {
    setUserInfo(userData)
    updateUserStatusPopUp()
  }

  const onViewActivityLogHandler = (userData: usersListData) => {
    setActivityLogUserId(userData.userId)
    setActivityLogUserName(userData.userName)
    setShowActivityLog(true)
  }

  const onHideActivityLog = () => {
    setShowActivityLog(false)
  }

  const handleApplyFilter = useCallback(() => {
    setPageNumber(1)
    getUsersList({
      offSet: 0,
      limit,
      searchQuery: query,
      userStatus: status,
    })
  }, [limit, query, status])

  return {
    t,
    componentsIdMapping,
    showAddUserPopUp,
    showAddUser,
    closeAddUserPopUp,
    onStatusChangeHandler,
    queryHandler,
    handleResetFilter,
    handlePageSize,
    fetchData,
    onEditHandler,
    getUserModalMessage,
    onStatusHandler,
    status,
    userInfo,
    query,
    pageNumber,
    limit,
    usersList,
    handleApplyFilter,
    isEdit,
    userId,
    updateUserStatusPopUp,
    updateUserAccountState,
    updateUserStatus,
    viewportHeight,
    showActivityLog,
    activityLogUserId,
    activityLogUserName,
    onViewActivityLogHandler,
    onHideActivityLog,
  }
}
export default useAddUserHook
